import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import styled from "styled-components";
import Back from "sushiweb/Icons/all/Back";
import Button from "sushiweb/Button";
import Breadcrumb from "sushiweb/Breadcrumb";
import Input from "sushiweb/InputTypes/Input";
import Textarea from "sushiweb/InputTypes/Textarea";
import ToggleSwitch from "../../components/Common/ToggleSwitch";
import PaginatedReactDropdown from "../../components/Common/PaginatedReactDropdown";
import {
  FlexContainer,
  Heading,
  FieldsContainer,
} from "../../constants/assets/containers";
import variables from "../../constants/assets/variables";
import * as SubIssueTypeActions from "../../store/actions/SubIssueTypes";
import * as IssueTypesActions from "../../store/actions/IssueTypes";
import * as UserActions from "../../store/actions/User";
import Permission from "../../components/Common/Permission";
import MenuNames from "../../constants/Menu/MenuNames.json";
import MenuActionsMappings from "../../constants/Menu/MenuActionsMappings";

const SubIssueTipsAction = (props) => {
  const { getSubIssueTypesDetails, getIssueArea, setStatus } = props;
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [tipData, setTipData] = useState(null);
  const [inputFieldState, setInputFieldState] = useState({});
  const [initialTipData, setInitialTipData] = useState(null);
  const [actionTypeOptions, setActionTypeOptions] = useState([]);
  const [connotationOptions, setConnotationOptions] = useState([]);
  const [conditionsConfig, setConditionsConfig] = useState([]);

  const { id, tipId, action } = useParams();
  const subIssueTypeId = id;

  const type =
    action ||
    (history.location.pathname.includes("/create") ? "create" : "view");
  const [actionType, setActionType] = useState(type);
  const allActions = MenuActionsMappings[MenuNames.LOGISTICS_SUPPORT_DASHBOARD];

  useEffect(() => {
    if (
      !conditionsConfig.length ||
      !actionTypeOptions.length ||
      !connotationOptions.length
    ) {
      console.log("Fetching configurations from API...");
      getIssueArea()
        .then((response) => {
          if (response && response.actionTypes) {
            const formattedActionTypes = response.actionTypes.map((type) => ({
              id: type,
              name: type,
            }));
            setActionTypeOptions(formattedActionTypes);
          }

          if (response && response.connotations) {
            const formattedConnotations = response.connotations.map(
              (connotation) => ({
                id: connotation.toUpperCase(),
                name: connotation,
              })
            );
            setConnotationOptions(formattedConnotations);
          }

          if (response && response.conditions) {
            setConditionsConfig(response.conditions);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch configurations:", err);
          setStatus({
            type: "error",
            message: "Failed to load configurations. Please refresh the page.",
          });
        });
    }
  }, [getIssueArea, setStatus]);

  const transformConditionsToV2 = (conditions) => {
    const conditionsV2 = [];

    if (!conditions || !Array.isArray(conditions)) {
      return [];
    }

    conditions.forEach((condition) => {
      if (!condition) return;

      if (
        !condition.hasOwnProperty("key") &&
        !condition.hasOwnProperty("value")
      ) {
        conditionsV2.push(condition);
        return;
      }

      const { key, value } = condition;

      if (!key || key.trim() === "") {
        return;
      }

      const configItem = conditionsConfig.find((c) => c.name === key);
      if (!configItem) return;

      const dataType = configItem.data_type;

      if (
        (dataType === "string" && (value === "" || value.trim() === "")) ||
        (dataType === "[]string" &&
          Array.isArray(value) &&
          value.length === 0) ||
        value === undefined ||
        value === null
      ) {
        return;
      }

      const conditionObj = {};
      conditionObj[key] = value;
      conditionsV2.push(conditionObj);
    });

    return conditionsV2;
  };

  useEffect(() => {
    setActionType(type);

    if (type === "create") {
      setTipData(null);
      setInputFieldState({
        name: "",
        description: "",
        ctaName: "",
        actionType: "",
        headingText: "Try this",
        connotation: "NEUTRAL",
        collectRating: true,
        enabled: true,
        raiseTicketEnabled: false,
        rank: 1,
        disQualifierContent: "",
      });
      setInitialTipData(null);
      setIsLoading(false);
      return;
    }

    if (history.location.state?.tipData) {
      const tip = history.location.state.tipData;
      setTipData(tip);
      setInputFieldState({ ...tip });
      setInitialTipData({ ...tip });
      setIsLoading(false);
      return;
    }

    if (subIssueTypeId) {
      if (!tipId || tipId === "undefined") {
        history.replace(`/sub-issue-types/${subIssueTypeId}/tips`);
        return;
      }

      setIsLoading(true);
      getSubIssueTypesDetails(subIssueTypeId)
        .then((response) => {
          const tips = response?.data?.tipsMappings || [];

          const matchingTip =
            tips[tipId] ||
            tips.find((tip) => tip.id && tip.id.toString() === tipId);

          if (matchingTip) {
            setTipData(matchingTip);
            setInputFieldState({ ...matchingTip });
            setInitialTipData({ ...matchingTip });
          } else {
            setStatus({
              type: "error",
              message: "Tip not found with the provided information",
            });

            history.replace(`/sub-issue-types/${subIssueTypeId}/tips`);
          }
          setIsLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching tip details:", error);
          setStatus({
            type: "error",
            message: "Failed to fetch tip details",
          });
          setIsLoading(false);

          history.replace(`/sub-issue-types/${subIssueTypeId}/tips`);
        });
    }
  }, [
    tipId,
    subIssueTypeId,
    getSubIssueTypesDetails,
    history.location.state,
    setStatus,
    type,
  ]);

  const handleBackClick = () => {
    history.push(`/sub-issue-types/${subIssueTypeId}/tips`);
  };

  const handleButtonClick = (action) => {
    if (action === "edit") {
      setActionType("edit");
    } else if (action === "create" || action === "save") {
      if (validateFields()) {
        setIsLoading(true);

        getSubIssueTypesDetails(subIssueTypeId)
          .then((response) => {
            if (response && response.data) {
              const subIssueData = response.data;
              const updatedTipData = { ...inputFieldState };

              if (updatedTipData.conditions) {
                let conditionsArray;

                if (typeof updatedTipData.conditions === "string") {
                  try {
                    conditionsArray = JSON.parse(updatedTipData.conditions);
                  } catch (e) {
                    console.error("Error parsing conditions:", e);
                    conditionsArray = [];
                  }
                } else if (Array.isArray(updatedTipData.conditions)) {
                  conditionsArray = updatedTipData.conditions;
                } else {
                  conditionsArray = [];
                }

                updatedTipData.conditionsV2 =
                  transformConditionsToV2(conditionsArray);
              }

              if (action === "create") {
                const newTip = updatedTipData;

                delete newTip.id;

                const updatedTips = [
                  ...(subIssueData.tipsMappings || []),
                  newTip,
                ];
                let transformedSubIssueConditionsV2 = [];
                if (
                  subIssueData.conditionsV2 &&
                  Array.isArray(subIssueData.conditionsV2)
                ) {
                  transformedSubIssueConditionsV2 = transformConditionsToV2(
                    subIssueData.conditionsV2
                  );
                }

                const payload = {
                  ...subIssueData,
                  tipsMappings: updatedTips,
                  conditionsV2: transformedSubIssueConditionsV2,
                };

                props
                  .saveUpdateSubIssueTypes("edit", payload)
                  .then((result) => {
                    setIsLoading(false);
                    if (result) {
                      history.push(`/sub-issue-types/${subIssueTypeId}/tips`);
                      setStatus({
                        type: "success",
                        message: "Tip created successfully",
                      });
                    } else {
                      setStatus({
                        type: "error",
                        message: "Failed to create tip",
                      });
                    }
                  })
                  .catch((error) => {
                    console.error("Error creating tip:", error);
                    setIsLoading(false);
                    setStatus({
                      type: "error",
                      message: "Failed to create tip",
                    });
                  });
              } else {
                const updatedTips = subIssueData.tipsMappings.map((tip) =>
                  tip.id === updatedTipData.id ? updatedTipData : tip
                );

                let transformedSubIssueConditionsV2 = [];
                if (
                  subIssueData.conditionsV2 &&
                  Array.isArray(subIssueData.conditionsV2)
                ) {
                  transformedSubIssueConditionsV2 = transformConditionsToV2(
                    subIssueData.conditionsV2
                  );
                }

                const payload = {
                  ...subIssueData,
                  tipsMappings: updatedTips,
                  conditionsV2: transformedSubIssueConditionsV2,
                };

                const actionType = "edit";
                props
                  .saveUpdateSubIssueTypes(actionType, payload)
                  .then((result) => {
                    setIsLoading(false);
                    if (result) {
                      setActionType("view");
                      setTipData(inputFieldState);
                      setInitialTipData({ ...inputFieldState });
                      setStatus({
                        type: "success",
                        message: "Tip updated successfully",
                      });
                    } else {
                      setStatus({
                        type: "error",
                        message: "Failed to update tip",
                      });
                    }
                  })
                  .catch((error) => {
                    console.error("Error updating tip:", error);
                    setIsLoading(false);
                    setStatus({
                      type: "error",
                      message: "Failed to update tip",
                    });
                  });
              }
            }
          })
          .catch((error) => {
            console.error("Error fetching latest sub-issue data:", error);
            setIsLoading(false);
            setStatus({
              type: "error",
              message: "Failed to fetch latest sub-issue data",
            });
          });
      }
    } else if (action === "cancel") {
      if (actionType === "create") {
        history.push(`/sub-issue-types/${subIssueTypeId}/tips`);
      } else {
        setInputFieldState({ ...initialTipData });
        setActionType("view");
      }
    }
  };

  const validateFields = () => {
    if (!inputFieldState.name || inputFieldState.name.trim() === "") {
      setStatus({
        type: "error",
        message: "Name is required",
      });
      return false;
    }

    if (
      !inputFieldState.description ||
      inputFieldState.description.trim() === ""
    ) {
      setStatus({
        type: "error",
        message: "Description is required",
      });
      return false;
    }

    return true;
  };

  const handleChange = (key, val) => {
    if (
      key === "enabled" ||
      key === "collectRating" ||
      key === "raiseTicketEnabled"
    ) {
      setInputFieldState((prevState) => ({
        ...prevState,
        [key]: !prevState[key],
      }));
    } else {
      setInputFieldState((prevState) => ({
        ...prevState,
        [key]: val,
      }));
    }
  };

  const BreadCrumbBar = () => {
    return (
      <>
        <Back
          onClick={handleBackClick}
          className="mr-10 backBtn"
          color={variables.grey400}
        />
        <Breadcrumb className="sushi-breadcrumbs">
          <Breadcrumb.Link onClick={handleBackClick}>
            Sub Issue Tips
          </Breadcrumb.Link>
          {actionType === "create" ? (
            <Breadcrumb.Link>Create New Tip</Breadcrumb.Link>
          ) : (
            <>
              <Breadcrumb.Link>{tipId}</Breadcrumb.Link>
              <Breadcrumb.Link>{actionType}</Breadcrumb.Link>
            </>
          )}
        </Breadcrumb>
      </>
    );
  };

  const RightActionButtons = () => {
    if (actionType === "create") {
      return (
        <>
          <Button
            data-testid="createTipBtn"
            onClick={() => handleButtonClick("create")}
            className="sushi-btn mr-20"
          >
            Create
          </Button>
          <Button
            data-testid="cancelTipBtn"
            onClick={() => handleButtonClick("cancel")}
            className="sushi-btn"
            appearance="outline"
          >
            Cancel
          </Button>
        </>
      );
    } else if (actionType === "view") {
      return (
        <>
          <Permission requiredPerms={[allActions.UPDATE_SUB_ISSUE_TYPE]}>
            <Button
              data-testid="editTipBtn"
              onClick={() => handleButtonClick("edit")}
              className="sushi-btn"
              appearance="outline"
            >
              Edit
            </Button>
          </Permission>
        </>
      );
    } else {
      return (
        <>
          <Button
            data-testid="saveTipBtn"
            onClick={() => handleButtonClick("save")}
            className="sushi-btn mr-20"
          >
            Save
          </Button>
          <Button
            data-testid="cancelTipBtn"
            onClick={() => handleButtonClick("cancel")}
            className="sushi-btn"
            appearance="outline"
          >
            Cancel
          </Button>
        </>
      );
    }
  };

  const renderViewMode = () => {
    return (
      <FieldsContainer border noPadding className="mt-20">
        <FieldsHolder>
          <FieldsSection marginBottom="1.75rem">
            <p className="section-heading">BASIC DETAILS</p>
          </FieldsSection>

          <FieldRow>
            <FieldLabel>ID</FieldLabel>
            <FieldValue>{tipData.id}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Name</FieldLabel>
            <FieldValue>{tipData.name}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Description</FieldLabel>
            <FieldValue>{tipData.description}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>CTA Name</FieldLabel>
            <FieldValue>{tipData.ctaName}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Action Type</FieldLabel>
            <FieldValue>{tipData.actionType}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Heading Text</FieldLabel>
            <FieldValue>{tipData.headingText}</FieldValue>
          </FieldRow>
        </FieldsHolder>

        <Divider height="6px" color={variables.grey100} />

        <FieldsHolder>
          <FieldsSection marginBottom="1.75rem">
            <p className="section-heading">DISPLAY SETTINGS</p>
          </FieldsSection>

          <FieldRow>
            <FieldLabel>Connotation</FieldLabel>
            <FieldValue>{tipData.connotation}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Rank</FieldLabel>
            <FieldValue>{tipData.rank}</FieldValue>
          </FieldRow>
        </FieldsHolder>

        <Divider height="6px" color={variables.grey100} />

        <FieldsHolder>
          <FieldsSection marginBottom="1.75rem">
            <p className="section-heading">BEHAVIOR SETTINGS</p>
          </FieldsSection>

          <FieldRow>
            <FieldLabel>Collect Rating</FieldLabel>
            <FieldValue>{tipData.collectRating ? "Yes" : "No"}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Enabled</FieldLabel>
            <FieldValue>{tipData.enabled ? "Yes" : "No"}</FieldValue>
          </FieldRow>

          <FieldRow>
            <FieldLabel>Raise Ticket Enabled</FieldLabel>
            <FieldValue>{tipData.raiseTicketEnabled ? "Yes" : "No"}</FieldValue>
          </FieldRow>
          <FieldRow>
            <FieldLabel>Dis Qualifier Content</FieldLabel>
            <FieldValue>{tipData.disQualifierContent || "-"}</FieldValue>
          </FieldRow>
        </FieldsHolder>
      </FieldsContainer>
    );
  };

  const renderEditMode = () => {
    return (
      <FieldsContainer border noPadding className="mt-20">
        <FieldsHolder>
          <FieldsSection marginBottom="1.75rem">
            <p className="section-heading">BASIC DETAILS</p>
          </FieldsSection>

          {actionType !== "create" && (
            <Input
              value={inputFieldState.id}
              data-testid="tipFieldId"
              id="sushi-input"
              disabled={true}
              label="ID"
              innerWidth="20%"
            />
          )}

          <Input
            value={inputFieldState.name}
            data-testid="tipFieldName"
            id="sushi-input"
            onChange={(e) => handleChange("name", e)}
            label="Name"
            innerWidth="50%"
          />

          <Textarea
            id="sushi-textarea"
            data-testid="tipFieldDescription"
            borderColor={variables.primaryColor}
            innerWidth="80%"
            resize="none"
            rows={4}
            dynamicHeight={true}
            label="Description"
            onChange={(e) => handleChange("description", e)}
            value={inputFieldState.description}
          />

          <Input
            value={inputFieldState.ctaName}
            data-testid="tipFieldCtaName"
            id="sushi-input"
            onChange={(e) => handleChange("ctaName", e)}
            label="CTA Name"
            innerWidth="50%"
          />

          <PaginatedReactDropdown
            options={actionTypeOptions}
            handleChange={handleChange}
            keyToChange="actionType"
            placeholder="Action Type"
            showCross={true}
            id="tipActionTypeField"
            width="50%"
            labelWidth="auto"
            value={inputFieldState.actionType}
            labelKey="name"
            valueKey="id"
          />

          <Input
            value={inputFieldState.headingText}
            data-testid="tipFieldHeadingText"
            id="sushi-input"
            onChange={(e) => handleChange("headingText", e)}
            label="Heading Text"
            innerWidth="70%"
          />
          <Textarea
            id="sushi-disqualifier-textarea"
            data-testid="tipFieldDisQualifierContent"
            borderColor={variables.primaryColor}
            innerWidth="80%"
            resize="none"
            rows={3}
            dynamicHeight={true}
            label="Dis Qualifier Content"
            onChange={(e) => handleChange("disQualifierContent", e)}
            value={inputFieldState.disQualifierContent || ""}
            className="mt-20"
          />
        </FieldsHolder>

        <Divider height="6px" color={variables.grey100} />

        <FieldsHolder>
          <FieldsSection marginBottom="1.75rem">
            <p className="section-heading">DISPLAY SETTINGS</p>
          </FieldsSection>

          <PaginatedReactDropdown
            options={connotationOptions}
            handleChange={handleChange}
            keyToChange="connotation"
            placeholder="Connotation"
            showCross={true}
            id="tipConnotationField"
            width="50%"
            labelWidth="auto"
            value={inputFieldState.connotation}
            labelKey="name"
            valueKey="id"
          />

          <Input
            value={inputFieldState.rank}
            type="number"
            data-testid="tipFieldRank"
            id="sushi-input"
            onChange={(e) => handleChange("rank", e)}
            label="Rank"
            innerWidth="30%"
          />
        </FieldsHolder>

        <Divider height="6px" color={variables.grey100} />

        <FieldsHolder>
          <FieldsSection marginBottom="1.75rem">
            <p className="section-heading">BEHAVIOR SETTINGS</p>
          </FieldsSection>

          <ToggleSwitch
            className="text-muted mb-20"
            label="Collect Rating"
            id="sushi-switch-collectRating"
            size={10}
            spacing={4}
            isActive={inputFieldState.collectRating}
            handleChange={() => handleChange("collectRating")}
            keyName="collectRating"
            changeOnState={true}
          />

          <ToggleSwitch
            className="text-muted mb-20"
            label="Enabled"
            id="sushi-switch-enabled"
            size={10}
            spacing={4}
            isActive={inputFieldState.enabled}
            handleChange={() => handleChange("enabled")}
            keyName="enabled"
            changeOnState={true}
          />

          <ToggleSwitch
            className="text-muted"
            label="Raise Ticket Enabled"
            id="sushi-switch-raiseTicketEnabled"
            size={10}
            spacing={4}
            isActive={inputFieldState.raiseTicketEnabled}
            handleChange={() => handleChange("raiseTicketEnabled")}
            keyName="raiseTicketEnabled"
            changeOnState={true}
          />
        </FieldsHolder>
      </FieldsContainer>
    );
  };

  return (
    <SubIssueTypeActionContainer>
      {isLoading ? (
        <div>Loading tip details...</div>
      ) : (
        <>
          <FlexContainer>
            <LeftContainer justify="flex-start">
              {BreadCrumbBar()}
            </LeftContainer>
            <RightContainer flexpct="25">{RightActionButtons()}</RightContainer>
          </FlexContainer>

          <Heading className="mt-20 mb-20">
            {actionType === "create" ? "Create New Tip" : "Tip Details"}
          </Heading>

          {actionType === "create" ? (
            renderEditMode()
          ) : tipData ? (
            actionType === "view" ? (
              renderViewMode()
            ) : (
              renderEditMode()
            )
          ) : (
            <div>No tip data available.</div>
          )}
        </>
      )}
    </SubIssueTypeActionContainer>
  );
};

const SubIssueTypeActionContainer = styled.section`
  padding: 20px;
`;

const LeftContainer = styled(FlexContainer)`
  flex: 0 1 30%;
  margin-bottom: 20px;
  & > div {
    flex-direction: row;
  }
`;

const RightContainer = styled(FlexContainer)`
  flex: ${({ flexpct }) => (flexpct ? `0 1 ${flexpct}%` : "0 1 35%")};
  justify-content: flex-end;
`;

const FieldsHolder = styled.div`
  padding: 1.875rem;
`;

const FieldsSection = styled.div`
  margin-bottom: ${({ marginBottom }) => marginBottom || "1rem"};
  .section-heading {
    font-size: 0.875rem;
    letter-spacing: 0.18rem;
    color: ${variables.grey600};
    text-transform: uppercase;
  }
`;

const FieldRow = styled.div`
  display: flex;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid ${variables.grey200};
`;

const FieldLabel = styled.div`
  width: 30%;
  min-width: 200px;
  font-weight: 600;
  color: ${variables.grey700};
`;

const FieldValue = styled.div`
  flex: 1;
`;

const Divider = styled.div`
  height: ${({ height }) => height || "1px"};
  background-color: ${({ color }) => color || variables.grey200};
`;

function mapStateToProps(state) {
  return {};
}

function mapDispatchToProps(dispatch) {
  return {
    getSubIssueTypesDetails: bindActionCreators(
      SubIssueTypeActions.getSubIssueTypesDetails,
      dispatch
    ),
    saveUpdateSubIssueTypes: bindActionCreators(
      SubIssueTypeActions.saveUpdateSubIssueTypes,
      dispatch
    ),
    getIssueArea: bindActionCreators(IssueTypesActions.getIssueArea, dispatch),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(SubIssueTipsAction);
